import React, { useState } from 'react';
import { API_URL } from '../config';

export default function IcoOwnerChecker({ onClose, isDarkMode }) {
  const [icoInput, setIcoInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Zpracuj vstup a najdi vlastníky
  const checkOwners = async () => {
    // Extrahuj všechna IČO z textu (8 číslic)
    const icoPattern = /\b\d{8}\b/g;
    const icos = icoInput.match(icoPattern);
    
    if (!icos || icos.length === 0) {
      alert('Nebyla nalezena žádná platná IČO (8 číslic)');
      return;
    }

    // Odstraň duplicity
    const uniqueIcos = [...new Set(icos)];
    
    setLoading(true);
    setProgress({ current: 0, total: uniqueIcos.length });
    setResults([]);

    const resultsData = [];

    try {
      // Načti všechny uživatele pro zobrazení jmen
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      const users = usersRes.ok ? await usersRes.json() : [];

      // Pro každé IČO zjisti vlastníka
      for (let i = 0; i < uniqueIcos.length; i++) {
        const ico = uniqueIcos[i];
        setProgress({ current: i + 1, total: uniqueIcos.length });

        try {
          // Použij endpoint pro kontrolu duplicit
          const currentUserId = localStorage.getItem('lastUserId') || '';
          const response = await fetch(`${API_URL}/check-duplicates`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ icos: [ico], userId: currentUserId })
          });

          if (response.ok) {
            const data = await response.json();
            
            if (data.duplicates && data.duplicates.length > 0) {
              // IČO existuje v CRM
              const companies = [];
              
              // Pro každou nalezenou firmu získej detail včetně vlastníka
              for (const duplicate of data.duplicates) {
                try {
                  const detailRes = await fetch(`${API_URL}/companies/${duplicate.existingCompany.id}?userId=${localStorage.getItem('lastUserId') || ''}`);
                  if (detailRes.ok) {
                    const companyDetail = await detailRes.json();
                    
                    let ownerName = 'Neznámý';
                    if (companyDetail.createdBy) {
                      if (typeof companyDetail.createdBy === 'object' && companyDetail.createdBy.name) {
                        ownerName = companyDetail.createdBy.name;
                      } else if (typeof companyDetail.createdBy === 'string') {
                        const owner = users.find(u => u._id === companyDetail.createdBy);
                        ownerName = owner ? owner.name || owner.username : companyDetail.createdBy;
                      }
                    }
                    
                    companies.push({
                      id: companyDetail._id,
                      name: companyDetail.name,
                      owner: ownerName,
                      status: companyDetail.status,
                      address: companyDetail.address
                    });
                  }
                } catch (err) {
                  console.error('Chyba při načítání detailu firmy:', err);
                }
              }
              
              resultsData.push({
                ico,
                found: true,
                companies
              });
            } else {
              // IČO neexistuje v CRM
              resultsData.push({
                ico,
                found: false,
                companies: []
              });
            }
          }
        } catch (err) {
          console.error(`Chyba při kontrole IČO ${ico}:`, err);
          resultsData.push({
            ico,
            found: false,
            error: true,
            companies: []
          });
        }
      }

      setResults(resultsData);
    } catch (error) {
      console.error('Chyba při načítání dat:', error);
      alert('Chyba při načítání dat z CRM');
    } finally {
      setLoading(false);
    }
  };

  // Exportuj výsledky jako CSV pro Excel
  const exportToExcel = () => {
    // CSV hlavička
    let csv = 'IČO;Vlastník;Název firmy;Stav;Adresa\n';
    
    results.forEach(result => {
      if (result.error) {
        csv += `${result.ico};CHYBA;;;;\n`;
      } else if (result.found && result.companies.length > 0) {
        result.companies.forEach(company => {
          // Escapuj hodnoty pro CSV (nahraď středníky a uvozovky)
          const ico = result.ico;
          const owner = company.owner.replace(/;/g, ',').replace(/"/g, '""');
          const name = (company.name || '').replace(/;/g, ',').replace(/"/g, '""');
          const status = (company.status || '').replace(/;/g, ',').replace(/"/g, '""');
          const address = (company.address || '').replace(/;/g, ',').replace(/"/g, '""');
          
          csv += `${ico};${owner};${name};${status};${address}\n`;
        });
      } else {
        csv += `${result.ico};NENALEZENO V CRM;;;\n`;
      }
    });
    
    // Vytvoř Blob s BOM pro správné kódování v Excelu
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    
    // Vytvoř odkaz a stáhni soubor
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `kontrola_ico_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Exportuj výsledky jako text (pro schránku)
  const exportResults = () => {
    let text = '=== VÝSLEDKY KONTROLY IČO ===\n\n';
    
    results.forEach(result => {
      text += `IČO: ${result.ico}\n`;
      
      if (result.error) {
        text += '  Stav: Chyba při načítání\n';
      } else if (result.found && result.companies.length > 0) {
        text += `  Stav: Nalezeno (${result.companies.length}×)\n`;
        result.companies.forEach((company, index) => {
          text += `  ${index + 1}. ${company.name}\n`;
          text += `     Vlastník: ${company.owner}\n`;
          text += `     Stav: ${company.status || 'Bez stavu'}\n`;
          if (company.address) {
            text += `     Adresa: ${company.address}\n`;
          }
        });
      } else {
        text += '  Stav: Nenalezeno v CRM\n';
      }
      
      text += '\n';
    });
    
    // Statistika
    const found = results.filter(r => r.found && r.companies.length > 0).length;
    const notFound = results.filter(r => !r.found).length;
    const errors = results.filter(r => r.error).length;
    
    text += `\n=== STATISTIKA ===\n`;
    text += `Celkem zkontrolováno: ${results.length}\n`;
    text += `Nalezeno v CRM: ${found}\n`;
    text += `Nenalezeno v CRM: ${notFound}\n`;
    if (errors > 0) {
      text += `Chyby: ${errors}\n`;
    }
    
    // Zkopíruj do schránky
    navigator.clipboard.writeText(text);
    alert('Výsledky zkopírovány do schránky!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`max-w-6xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                🔎 Kontrola vlastníků podle IČO
              </h2>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {loading 
                  ? `Kontroluji IČO... (${progress.current}/${progress.total})`
                  : results.length > 0
                    ? `Zkontrolováno ${results.length} IČO`
                    : 'Vložte seznam IČO pro zjištění vlastníků v CRM'
                }
              </p>
            </div>
            <div className="flex gap-2">
              {results.length > 0 && (
                <>
                  <button
                    onClick={exportToExcel}
                    className={`px-4 py-2 rounded font-semibold transition ${
                      isDarkMode 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                    title="Stáhnout jako CSV soubor pro Excel"
                  >
                    📊 Stáhnout Excel
                  </button>
                  <button
                    onClick={exportResults}
                    className={`px-4 py-2 rounded font-semibold transition ${
                      isDarkMode 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                    title="Zkopírovat výsledky do schránky"
                  >
                    📋 Kopírovat text
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded font-semibold transition ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                }`}
              >
                ✕ Zavřít
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-120px)]">
          {/* Left panel - Input */}
          <div className={`w-1/2 border-r overflow-y-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-4">
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vstup - Seznam IČO
              </h3>
              
              <div className="space-y-3">
                <textarea
                  value={icoInput}
                  onChange={(e) => setIcoInput(e.target.value)}
                  placeholder="Vložte IČO (každé na nový řádek nebo oddělené mezerami)&#10;Např:&#10;12345678&#10;87654321&#10;nebo: 12345678, 87654321, 11223344"
                  className={`w-full h-64 px-3 py-2 border rounded font-mono text-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  disabled={loading}
                />
                
                <button
                  onClick={checkOwners}
                  disabled={loading || !icoInput.trim()}
                  className={`w-full px-4 py-3 rounded font-semibold transition ${
                    loading || !icoInput.trim()
                      ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                      : isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {loading ? '⏳ Kontroluji...' : '🔍 Zkontrolovat vlastníky'}
                </button>
                
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <p className="font-semibold mb-1">💡 Nápověda:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Můžete vložit IČO v jakémkoliv formátu</li>
                    <li>Systém automaticky najde všechna 8-místná čísla</li>
                    <li>Duplicitní IČO jsou automaticky odstraněna</li>
                    <li>Pro každé IČO se zobrazí všichni vlastníci v CRM</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right panel - Results */}
          <div className="w-1/2 overflow-y-auto">
            <div className="p-4">
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Výsledky
              </h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Kontroluji IČO v CRM...
                  </p>
                </div>
              ) : results.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="text-4xl mb-4">👈</div>
                  <p>Vložte seznam IČO a klikněte na "Zkontrolovat vlastníky"</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.map((result) => (
                    <div
                      key={result.ico}
                      className={`p-4 rounded border ${
                        result.error
                          ? isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                          : result.found && result.companies.length > 0
                            ? isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
                            : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            IČO: {result.ico}
                          </p>
                          {result.error ? (
                            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                              ⚠️ Chyba při načítání
                            </p>
                          ) : result.found && result.companies.length > 0 ? (
                            <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>
                              ✅ Nalezeno v CRM ({result.companies.length}×)
                            </p>
                          ) : (
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              ❌ Nenalezeno v CRM
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {result.found && result.companies.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {result.companies.map((company, index) => (
                            <div
                              key={company.id}
                              className={`p-3 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                            >
                              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {index + 1}. {company.name}
                              </p>
                              <div className={`text-sm mt-1 space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                <p>
                                  <span className="font-medium">👤 Vlastník:</span>{' '}
                                  <span className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                    {company.owner}
                                  </span>
                                </p>
                                {company.status && (
                                  <p>
                                    <span className="font-medium">📊 Stav:</span> {company.status}
                                  </p>
                                )}
                                {company.address && (
                                  <p>
                                    <span className="font-medium">📍 Adresa:</span> {company.address}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Statistika */}
                  <div className={`mt-4 p-4 rounded border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                    <p className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      📊 Statistika
                    </p>
                    <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p>Celkem zkontrolováno: <strong>{results.length}</strong></p>
                      <p>Nalezeno v CRM: <strong className="text-green-600 dark:text-green-400">
                        {results.filter(r => r.found && r.companies.length > 0).length}
                      </strong></p>
                      <p>Nenalezeno v CRM: <strong className="text-gray-600 dark:text-gray-400">
                        {results.filter(r => !r.found).length}
                      </strong></p>
                      {results.some(r => r.error) && (
                        <p>Chyby: <strong className="text-red-600 dark:text-red-400">
                          {results.filter(r => r.error).length}
                        </strong></p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
